import { useState } from 'react';
import { TONE_INK, TONE_OKLCH } from '@frisk/shared';
import { t } from '../theme';
import { useGroupOf, useStore } from '../store';
import { BestBeforeField } from './BestBeforeField';
import { IconTrash } from './icons';
import { QtyStepper } from './QtyStepper';
import { StockRule } from './StockRule';

export function ItemDetail() {
  const detailId = useStore((s) => s.detailId);
  const group = useGroupOf(detailId);
  // The sheet is about the product; the soonest batch is what it leads with.
  const item = group?.soonest;
  const childLock = useStore((s) => s.settings.childLock);
  const { openDetail, consume, discard, toShoppingList } = useStore.getState();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  if (!detailId || !group || !item) return null;

  // Kindermodus: deleting takes two deliberate taps rather than one stray one.
  const onDelete = () => {
    if (childLock && !confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    void discard(item.id);
  };

  return (
    <div
      onClick={() => openDetail(null)}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'oklch(0.12 0.01 60 / 0.66)',
        backdropFilter: 'blur(6px)',
        animation: 'fade 160ms ease-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 660,
          maxWidth: '88vw',
          borderRadius: 30,
          background: t.surface,
          border: '1px solid oklch(1 0 0 / 0.09)',
          padding: 28,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          animation: 'sheet 200ms ease-out',
        }}
      >
        <div style={{ display: 'flex', gap: 22, alignItems: 'center' }}>
          <div
            style={{
              width: 132,
              height: 132,
              flex: 'none',
              borderRadius: 22,
              background: group.imageUrl
                ? `center / cover no-repeat url(${group.imageUrl})`
                : 'repeating-linear-gradient(135deg, oklch(0.3 0.012 60) 0 8px, oklch(0.26 0.012 60) 8px 16px)',
            }}
          />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                display: 'inline-block',
                padding: '7px 14px',
                borderRadius: 999,
                background: TONE_OKLCH[item.expiry.tone],
                color: TONE_INK[item.expiry.tone],
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              {item.expiry.label}
            </div>
            <div style={{ fontSize: 36, fontWeight: 500, letterSpacing: '-0.045em', marginTop: 10, lineHeight: 1.15 }}>
              {item.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
              {group.batches.length === 1 ? (
                <QtyStepper batch={item} />
              ) : (
                group.totalQty > 1 && (
                  <span style={{ fontFamily: t.mono, fontSize: 14, letterSpacing: '0.1em', color: 'oklch(0.66 0.015 70)' }}>
                    {group.totalQty}×
                  </span>
                )
              )}
              {/* One batch: the header date is unambiguous, so it is the one
                  you edit. Several, and each gets its own field below. */}
              {group.batches.length === 1 && <BestBeforeField batch={item} />}
              {group.batches.length > 1 && (
                <span style={{ fontFamily: t.mono, fontSize: 14, letterSpacing: '0.1em', color: 'oklch(0.66 0.015 70)' }}>
                  ab {item.expiry.dateLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        {/*
          * Where several best-before dates stop being a problem and become the
          * point: the tile answers "do I have Nutella", this answers "which jar
          * do I open". Each batch is consumable on its own; the primary button
          * below takes the soonest.
          */}
        {group.batches.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {group.batches.map((batch, index) => (
              <div
                key={batch.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  borderRadius: 16,
                  background: index === 0 ? 'oklch(0.28 0.012 60)' : t.surfaceSunken,
                  border: index === 0 ? `1px solid ${TONE_OKLCH[batch.expiry.tone]}` : t.hairline,
                }}
              >
                <BestBeforeField batch={batch} compact />
                <div style={{ fontSize: 16, color: t.inkFaint, flex: 1, minWidth: 0 }}>
                  {batch.expiry.label}
                </div>
                <QtyStepper batch={batch} compact />
                <div
                  className="press"
                  onClick={() => void consume(batch.id)}
                  style={{
                    height: 40,
                    padding: '0 14px',
                    borderRadius: 12,
                    background: t.control,
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 16,
                    fontWeight: 500,
                    flex: 'none',
                  }}
                >
                  Verbraucht
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Configured on the product, where you are when you think "we keep
            running out of this" — not in a separate automation screen you
            have to remember exists. */}
        <StockRule name={group.name} barcode={item.barcode} />

        <div style={{ display: 'flex', gap: 12 }}>
          <div
            className="press"
            onClick={() => void consume(item.id)}
            style={{
              flex: 1.3,
              height: 82,
              borderRadius: 20,
              background: t.accent,
              color: t.accentInk,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 23,
              fontWeight: 600,
            }}
          >
            Verbraucht
          </div>
          <div
            className="press"
            onClick={() => void toShoppingList(item.name)}
            style={{
              flex: 1,
              height: 82,
              borderRadius: 20,
              background: 'oklch(0.3 0.012 60)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 21,
              fontWeight: 500,
              textAlign: 'center',
              padding: '0 12px',
            }}
          >
            Auf Einkaufsliste
          </div>
          <div
            className="press"
            onClick={onDelete}
            style={{
              width: confirmingDelete ? 190 : 92,
              flex: 'none',
              height: 82,
              borderRadius: 20,
              border: '1px solid oklch(0.72 0.17 25 / 0.55)',
              background: confirmingDelete ? 'oklch(0.3 0.06 25)' : 'transparent',
              color: 'oklch(0.82 0.14 30)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: confirmingDelete ? 19 : 26,
              fontWeight: confirmingDelete ? 600 : 400,
              textAlign: 'center',
              padding: '0 10px',
              transition: 'width 140ms',
            }}
          >
            {confirmingDelete ? 'Wirklich löschen?' : <IconTrash size={26} />}
          </div>
        </div>
      </div>
    </div>
  );
}
